import { collection, doc, addDoc, updateDoc, deleteDoc, getDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { workoutPrescriptionToFirestore } from "../../models/workout/workoutConverter";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "../audit_service";

const COLLECTION_NAME = "workoutPrescriptions";

/**
 * Cria prescrição(ões) — pode criar em lote (múltiplos alunos).
 * Usa workoutPrescriptionToFirestore para garantir padrão de dados.
 *
 * @param {Object} prescriptionBase - { idTemplate, templateTitle, workouts, startDate, endDate, weeklyFrequency }
 * @param {Array}  membersList      - [{ id, name, photo }]
 * @param {string} idTenant
 * @param {Object} authUser
 */
export const prescribe = async (prescriptionBase, membersList, idTenant, authUser) => {
    if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
    if (!membersList || membersList.length === 0) throw new Error("Selecione ao menos um aluno.");

    const results = [];

    try {
        let teacherEmail = authUser?.email || null;
        let teacherPhone = authUser?.phoneNumber || null;
        let teacherPhoto = authUser?.photoURL || null;
        let teacherName = authUser?.displayName || authUser?.email || "Sistema";

        if (authUser?.uid && (!teacherEmail || !teacherPhone || !teacherPhoto)) {
            const teacherRef = doc(db, "users", authUser.uid);
            const teacherSnap = await getDoc(teacherRef);
            if (teacherSnap.exists()) {
                const tData = teacherSnap.data();
                if (!teacherEmail) teacherEmail = tData.email || null;
                if (!teacherPhone) teacherPhone = tData.phone || null;
                if (!teacherPhoto) teacherPhoto = tData.photoUrl || null;
                if (teacherName === "Sistema" || teacherName === authUser.email) {
                    teacherName = tData.fullName || tData.name || teacherName;
                }
            }
        }

        for (const member of membersList) {
            let memberEmail = member.email || null;
            let memberPhone = member.phone || null;
            let memberPhoto = member.photo || member.photoUrl || null;

            if (!memberEmail || !memberPhone || !memberPhoto) {
                const memberRef = doc(db, "tenants", idTenant, "members", member.id);
                const memberSnap = await getDoc(memberRef);
                if (memberSnap.exists()) {
                    const mData = memberSnap.data();
                    if (!memberEmail) memberEmail = mData.email || null;
                    if (!memberPhone) memberPhone = mData.phone || null;
                    if (!memberPhoto) memberPhoto = mData.photoUrl || null;
                }
            }

            const enrichedMember = { ...member, email: memberEmail, phone: memberPhone, photo: memberPhoto };
            const teacherData = { teacherName, teacherEmail, teacherPhone, teacherPhoto, memberEmail, memberPhone, memberPhoto };

            const data = workoutPrescriptionToFirestore(prescriptionBase, enrichedMember, { id: idTenant }, authUser, teacherData);
            data.prescribedAt = serverTimestamp();
            data.updatedAt = serverTimestamp();

            const newDocRef = await addDoc(
                collection(db, "tenants", idTenant, "members", member.id, COLLECTION_NAME),
                data
            );
            results.push({ id: newDocRef.id, ...data });

            // Gerar sessões individuais a partir do campo canônico sessions
            const workouts = prescriptionBase.sessions?.length > 0
                ? prescriptionBase.sessions
                : (prescriptionBase.workouts || []);
            if (workouts.length > 0) {
                const totalExpected = data.totalExpectedSessions || 0;
                const isSingleDay = prescriptionBase.isSingleDay || false;
                const sessionsToGenerate = isSingleDay ? workouts.length : totalExpected;

                if (sessionsToGenerate > 0) {
                    const sessionsCol = collection(
                        db, "tenants", idTenant, "members", member.id,
                        COLLECTION_NAME, newDocRef.id, "workoutSessions"
                    );

                    const BATCH_LIMIT = 499;
                    let batch = writeBatch(db);
                    let batchCount = 0;

                    // Distribui sessões uniformemente na semana (ex: 3x = Seg/Qua/Sex)
                    const freq = prescriptionBase.weeklyFrequency || 3;
                    const daySpread = [];
                    for (let d = 0; d < freq; d++) {
                        daySpread.push(Math.round((d * 6) / Math.max(freq - 1, 1)));
                    }
                    if (freq === 1) daySpread[0] = 0;

                    for (let i = 0; i < sessionsToGenerate; i++) {
                        const workoutIndex = i % workouts.length;
                        const workout = workouts[workoutIndex];
                        const letter = String.fromCharCode(65 + workoutIndex); // A, B, C...

                        const weekNumber = Math.floor(i / freq);
                        const dayInWeek = i % freq;
                        let scheduledDate = null;
                        if (prescriptionBase.startDate) {
                            const baseDate = new Date(prescriptionBase.startDate + "T00:00:00");
                            baseDate.setDate(baseDate.getDate() + (weekNumber * 7) + (daySpread[dayInWeek] || 0));
                            scheduledDate = baseDate.toISOString().split("T")[0];
                        }

                        const sessionData = {
                            sessionNumber: i + 1,
                            totalSessions: sessionsToGenerate,
                            workoutIndex,
                            workoutName: workout.name || `Treino ${letter}`,
                            workoutLetter: letter,
                            scheduledDate,
                            weekNumber: weekNumber + 1,
                            blocks: (workout.blocks || []).map(block => ({
                                name: block.name || "",
                                workoutType: block.workoutType || prescriptionBase.workoutType || "strength",
                                method: block.method || "normal",
                                sets: (block.sets || []).map(set => ({
                                    ...set,
                                    actualLoad: null,
                                    actualReps: null,
                                    actualSets: null,
                                    notes: null,
                                }))
                            })),
                            status: "pending",
                            completedAt: null,
                            completedVia: null,
                            skippedAt: null,
                            idPrescription: newDocRef.id,
                            idTenant,
                            templateTitle: prescriptionBase.templateTitle || "",
                            idMember: member.id,
                            memberName: enrichedMember.name || member.name || "",
                            memberEmail: enrichedMember.email || null,
                            memberPhone: enrichedMember.phone || null,
                            memberPhoto: enrichedMember.photo || null,
                            idTeacher: authUser?.uid || null,
                            teacherName,
                            teacherEmail: teacherEmail || null,
                            teacherPhone: teacherPhone || null,
                            teacherPhoto: teacherPhoto || null,
                            startDate: prescriptionBase.startDate || null,
                            endDate: prescriptionBase.endDate || null,
                            weeklyFrequency: prescriptionBase.weeklyFrequency || null,
                            totalWeeks: prescriptionBase.totalWeeks || null,
                            isSingleDay: prescriptionBase.isSingleDay || false,
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                        };

                        const sessionDocRef = doc(sessionsCol);
                        batch.set(sessionDocRef, sessionData);
                        batchCount++;

                        if (batchCount >= BATCH_LIMIT) {
                            await batch.commit();
                            batch = writeBatch(db);
                            batchCount = 0;
                        }
                    }

                    if (batchCount > 0) {
                        await batch.commit();
                    }
                }
            }

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.WORKOUTS,
                action: AUDIT_ACTIONS.CREATE,
                entityId: newDocRef.id,
                entityName: `Prescrição: ${prescriptionBase.templateTitle}`,
                description: `Prescreveu "${prescriptionBase.templateTitle}" para ${member.name} (${prescriptionBase.startDate} → ${prescriptionBase.endDate})`
            });
        }

        return results;
    } catch (error) {
        console.error("Erro no WorkoutPrescriptionService.prescribe:", error);
        throw error;
    }
};

/**
 * Atualiza uma prescrição (ex: ajustar blocos ou status).
 */
export const update = async (prescriptionId, updateData, idTenant, authUser, idMember) => {
    if (!idTenant || !prescriptionId || !idMember) throw new Error("Tenant, Member e Prescription IDs são obrigatórios.");

    try {
        const docRef = doc(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME, prescriptionId);
        await updateDoc(docRef, {
            ...updateData,
            updatedAt: serverTimestamp(),
            updatedBy: {
                uid: authUser?.uid || "system",
                name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
            }
        });

        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.WORKOUTS,
            action: AUDIT_ACTIONS.UPDATE,
            entityId: prescriptionId,
            entityName: updateData.templateTitle || "Prescrição",
            description: `Atualizou prescrição "${updateData.templateTitle || prescriptionId}"`
        });

        return { id: prescriptionId, ...updateData };
    } catch (error) {
        console.error("Erro no WorkoutPrescriptionService.update:", error);
        throw error;
    }
};

/**
 * Remove uma prescrição.
 */
export const deletePrescription = async (prescriptionId, idTenant, authUser, prescriptionTitle, idMember) => {
    if (!idTenant || !prescriptionId || !idMember) throw new Error("Tenant, Member e Prescription IDs são obrigatórios.");

    try {
        await deleteDoc(doc(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME, prescriptionId));

        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.WORKOUTS,
            action: AUDIT_ACTIONS.DELETE,
            entityId: prescriptionId,
            entityName: prescriptionTitle || "Prescrição",
            description: `Excluiu prescrição "${prescriptionTitle || prescriptionId}"`
        });

        return true;
    } catch (error) {
        console.error("Erro no WorkoutPrescriptionService.delete:", error);
        throw error;
    }
};
